"use client";

import React, { useEffect, useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { saveAs } from "file-saver";
import toast from "react-hot-toast";

import Button from "../../button-v1";
import Spinner from "../../spinner";

import logo from "../../../assets/images/toca_health_pdf_logo.png";
import generateOORSummaryPDF from "./OORSummaryPDF";
import OORSummaryPreview from "./OORSummaryPreview";

import { getOutOfRangeReadings } from "../../../services/admin-case-manager/readings";
import { getPatientGeneralDetails } from "../../../services/patient/details";
import { convertUTCToLocal } from "../../../utils/helper";
import { buildReportFilename, DOCTYPES } from "../../../utils/fileNaming";

import styles from "../../../styles/oorPdf.module.css";

// Get categories for reading (from Charts.js)
const getCategoriesForReading = (r) => {
  const cats = [];

  if (r.category) cats.push(r.category);
  if (r.bp_category) cats.push(`BP: ${r.bp_category}`);
  if (r.heart_rate_category) cats.push(`HR: ${r.heart_rate_category}`);

  if (r.glucose && r.reading_type === "blood_glucose" && !r.category) {
    if (r.glucose >= 300) cats.push("Diabetes");
    else if (r.glucose >= 140) cats.push("Prediabetes");
  }

  if (r.oxygen && r.reading_type === "pulse_ox" && !r.category) {
    if (r.oxygen < 90) cats.push("Hypoxia");
    else cats.push("Normal");
  }

  if (r.heart_rate && r.reading_type === "heart_rate" && r.heart_rate_category) {
    cats.push(`HR: ${r.heart_rate_category}`);
  }

  return Array.from(new Set(cats.filter(Boolean)));
};

const deriveMonitoringCategory = (r) => {
  const cats = getCategoriesForReading(r);
  return cats.length ? cats.join(", ") : "—";
};

// Get reading type display name (from Charts.js)
const getReadingTypeDisplay = (readingType) => {
  const map = {
    blood_pressure: "Blood Pressure",
    weight: "Weight",
    temperature: "Temperature",
    pulse_ox: "Pulse Oximeter",
    blood_glucose: "Blood Glucose",
  };
  return map[readingType] || readingType || "—";
};

// Get reading value display (from Charts.js)
const getReadingValueDisplay = (r) => {
  switch (r.reading_type) {
    case "blood_pressure":
      return r.systolic && r.diastolic
        ? `${r.systolic} / ${r.diastolic} mmHg${r.heart_rate ? `, ${r.heart_rate} bpm` : ""}`
        : "—";
    case "weight":
      return r.weight ? `${r.weight} lbs` : "—";
    case "temperature":
      return r.temperature ? `${r.temperature} °C` : "—";
    case "pulse_ox":
      return r.oxygen
        ? `${r.oxygen}%${r.heart_rate ? `, ${r.heart_rate} bpm` : ""}`
        : "—";
    case "blood_glucose":
      return r.glucose ? `${r.glucose} mg/dL` : "—";
    default:
      return "—";
  }
};

const OORSummaryViewPdf = ({ currentUser }) => {
  const [params] = useSearchParams();
  const patientId = params.get("patientId");
  const reportRange = params.get("range") || "09/13/2025 to 09/19/2025";

  const [downloading, setDownloading] = useState(false);

  const {
    data: oorReadings,
    isLoading: isLoadingOor,
    isError: isErrorOor,
  } = useQuery(
    ["oor-summary", patientId],
    () => getOutOfRangeReadings(patientId),
    { 
      enabled: !!patientId
    }
  );

  const {
    data: patientDetails,
    isLoading: isLoadingPatient,
    isError: isErrorPatient,
  } = useQuery(
    ["patient-general", patientId],
    () => getPatientGeneralDetails(patientId),
    { 
      enabled: !!patientId
    }
  );

  const providerName = patientDetails?.provider
    ? `${patientDetails.provider.first_name || ""} ${patientDetails.provider.last_name || ""}`.trim() || "—"
    : "—";

  const records = oorReadings?.readings || [];

  const augmentedRecords = useMemo(() => {
    const patientName =
      patientDetails?.user
        ? `${patientDetails.user.first_name || ""} ${patientDetails.user.last_name || ""}`.trim()
        : null;

    const dob = patientDetails?.user?.dob || null;
    const office = patientDetails?.location?.name || "—";

    const augmented = records.map((r) => ({
      ...r,

      patient_name: r.patient_name || patientName || r.patient || "Unknown",
      dob: r.dob || dob || "—",
      office_location: r.office_location || office || "—",

      // Date, Type, Value, Category columns (matching Charts.js)
      date: r.recorded_at || r.created_at,
      type: getReadingTypeDisplay(r.reading_type),
      value: getReadingValueDisplay(r),
      category: deriveMonitoringCategory(r),

      monitoring_category: deriveMonitoringCategory(r),

      percent_time_oor: r.percent_time_oor ?? null,

      escalation_status:
        r.escalation_status ??
        (r.esclated ? "Escalated" : "__"),

      escalation_frequency: r.escalation_frequency ?? null,

      notes:
        r.notes ??
        r.review_notes ??
        r.note ??
        r.comment ??
        "—",

      timestamp: r.recorded_at || r.created_at,

      user_id: patientDetails?.id || "user-unknown",
    }));

   
    
    return augmented;
  }, [records, patientDetails, currentUser]);

  const sessionSummary = useMemo(() => {
    const totalReviewed = augmentedRecords.length;
    const totalEscalations = augmentedRecords.filter(
      (r) => (r.escalation_status || "").toLowerCase() === "escalated"
    ).length;

    return {
      userId: currentUser?.id || "user-unknown",
      role: currentUser?.role || "case-manager",
      sessionId: `sess-${Date.now()}`,
      completedAt: new Date().toISOString(),
      totalReviewed,
      totalEscalations,
      totalNonEscalations: totalReviewed - totalEscalations,
    };
  }, [augmentedRecords, currentUser]);

 
  const handleDownloadPDF = async () => {
    setDownloading(true);
    const toastId = toast.loading("Generating PDF...");
    try {
      // Convert logo to base64 if it's a path
      let logoBase64 = logo;
      if (logo && typeof logo === "string" && !logo.startsWith("data:")) {
        try {
          const response = await fetch(logo);
          const blob = await response.blob();
          const reader = new FileReader();
          logoBase64 = await new Promise((resolve) => {
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
        } catch (e) {
          // Could not convert logo to base64, using original
        }
      }

      const pdf = generateOORSummaryPDF({
        logo: logoBase64,
        reportRange,
        providerName,
        oorRecords: augmentedRecords,
        patientDetails,
        escalatedReadingsFrequency: patientDetails?.escalated_readings_frequency,
      });

      // Filename uses the download date (not reportRange — its "range" URL param
      // is never actually passed by the only caller, EsclatedReview.js, so it
      // would always resolve to a stale placeholder date).
      pdf.save(buildReportFilename({
        doctype: DOCTYPES.OOR_SUMMARY,
        program: "RPM",
        ext: "pdf",
      }));
      toast.success("PDF Downloaded", { id: toastId });
    } catch (err) {
      toast.error("Failed to generate PDF");
    } finally {
      setDownloading(false);
    }  
  };

  const isLoading = isLoadingOor || isLoadingPatient;

  if (isLoading) {
    return (
      <div style={{ padding: 24 }}>
        <Spinner />
      </div>
    );
  }

  if (isErrorOor || isErrorPatient) {
    return (
      <div style={{ padding: 24 }}>
        <p>Failed to load report data. Please try again.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 12, width: "70%" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <h3 style={{ margin: 0 }}></h3>

        <Button
          loading={downloading}
          disabled={downloading}
          onClick={handleDownloadPDF}
        >
          Download PDF
        </Button>
      </div>

      <div className={styles.previewOuter}>
        <OORSummaryPreview
          logo={logo}
          reportRange={reportRange}
          providerName={providerName}
          oorRecords={augmentedRecords}
          sessionSummary={sessionSummary}
          patientDetails={patientDetails}
          escalatedReadingsFrequency={patientDetails?.escalated_readings_frequency}
        />
      </div>
    </div>
  );
};

export default OORSummaryViewPdf;

